import { useState, useEffect, useRef, useCallback } from 'react';
import { captureFrame } from './captureFrame';
import { exposureOk, faceSizeOk } from './qualityChecks';

const HOLD_MS = 600;

export function useAutoCapture({
  isAligned,
  faceDetected,
  faceBox,
  videoRef,
  onCaptureResult,
  enabled = true
}) {
  const [state, setState] = useState("SEARCHING"); // SEARCHING, ALIGNING, HOLDING, CAPTURED
  const [holdProgress, setHoldProgress] = useState(0); // 0 to 1
  const [qualityWarning, setQualityWarning] = useState(null);

  const holdStartRef = useRef(null);
  const isCapturingRef = useRef(false);
  const animFrameRef = useRef(null);

  const performCapture = useCallback(async () => {
    const video = videoRef.current?.video || videoRef.current;
    if (!video || isCapturingRef.current) return;

    isCapturingRef.current = true;
    setState("CAPTURED");

    try {
      const result = await captureFrame(video, 0.95);
      if (onCaptureResult) {
        onCaptureResult(result);
      }
    } catch (err) {
      console.warn("Capture frame error:", err.message);
    } finally {
      setTimeout(() => {
        isCapturingRef.current = false;
        holdStartRef.current = null;
        setHoldProgress(0);
        setState("ALIGNING");
      }, 1000);
    }
  }, [videoRef, onCaptureResult]);

  useEffect(() => {
    if (!enabled || isCapturingRef.current) return;

    const video = videoRef.current?.video || videoRef.current;

    // 1. Check face detection
    if (!faceDetected) {
      setState("SEARCHING");
      setQualityWarning(null);
      holdStartRef.current = null;
      setHoldProgress(0);
      return;
    }

    // 2. Check quality killers: Face too small & exposure
    if (video && video.videoWidth) {
      if (faceBox && !faceSizeOk(faceBox.width, video.videoWidth)) {
        setState("ALIGNING");
        setQualityWarning("Move closer to the camera");
        holdStartRef.current = null;
        setHoldProgress(0);
        return;
      }

      if (!exposureOk(video)) {
        setState("ALIGNING");
        setQualityWarning("Move to better lighting");
        holdStartRef.current = null;
        setHoldProgress(0);
        return;
      }
    }
    setQualityWarning(null);

    // 3. Check pose alignment
    if (!isAligned) {
      setState("ALIGNING");
      holdStartRef.current = null;
      setHoldProgress(0);
      return;
    }

    // 4. Face is aligned and lighting/size are good -> HOLDING state
    setState("HOLDING");

    const tick = (now) => {
      if (!holdStartRef.current) {
        holdStartRef.current = now;
      }
      const elapsed = now - holdStartRef.current;
      const progress = Math.min(elapsed / HOLD_MS, 1);
      setHoldProgress(progress);

      if (elapsed >= HOLD_MS) {
        cancelAnimationFrame(animFrameRef.current);
        performCapture();
      } else {
        animFrameRef.current = requestAnimationFrame(tick);
      }
    };

    animFrameRef.current = requestAnimationFrame(tick);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [enabled, faceDetected, isAligned, faceBox, videoRef, performCapture]);

  return {
    state,
    holdProgress,
    qualityWarning,
    triggerManualCapture: performCapture
  };
}
